import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  CheckCircle2, XCircle, ArrowUpCircle, Send, ChevronDown, ChevronUp, MessageSquare, Mail,
} from "lucide-react";

const LANE_LABELS = {
  retail: "Retail", budget: "Budget", premium: "Premium", boutique: "Boutique", concierge: "Concierge",
};

const STATUS_OPTIONS = ["new", "booked", "pending_approval", "approved", "rejected", "consulted", "deposit_paid", "complete"];

function formatDate(d) {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ===== Row Actions =====
export function RowActions({ sub, onApprove, onReject, onUpgrade, onConciergeFinal, onFollowUp }) {
  return (
    <div className="flex flex-col gap-1.5">
      {sub.lane === "boutique" && sub.payment_status === "paid" && sub.status === "pending_approval" && (
        <div className="flex gap-1.5">
          <Button size="sm" onClick={() => onApprove(sub.id)} data-testid={`approve-${sub.id}`} className="h-6 px-2 text-xs" style={{ background: "var(--success)", color: "#fff" }}>
            <CheckCircle2 size={12} className="mr-1" /> Approve
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onReject(sub.id)} data-testid={`reject-${sub.id}`} className="h-6 px-2 text-xs" style={{ color: "var(--error)", border: "1px solid rgba(239,68,68,0.3)" }}>
            <XCircle size={12} className="mr-1" /> Reject
          </Button>
        </div>
      )}
      {(sub.lane === "budget" || sub.lane === "premium") && sub.payment_status === "paid" && !sub.upgrade_paid && !sub.upgrade_sent && (
        <Button size="sm" variant="ghost" onClick={() => onUpgrade(sub.id)} data-testid={`upgrade-${sub.id}`} className="h-6 px-2 text-xs" style={{ color: "var(--warning)", border: "1px solid rgba(245,158,11,0.3)" }}>
          <ArrowUpCircle size={12} className="mr-1" /> Send $100 Upgrade
        </Button>
      )}
      {sub.upgrade_sent && !sub.upgrade_paid && <span className="status-badge pending">Upgrade Pending</span>}
      {sub.upgrade_paid && <span className="status-badge paid">Upgraded</span>}
      {sub.lane === "concierge" && sub.concierge_deposit_paid && !sub.concierge_final_paid && (
        <Button size="sm" variant="ghost" onClick={() => onConciergeFinal(sub.id)} data-testid={`final-${sub.id}`} className="h-6 px-2 text-xs" style={{ color: "var(--accent)", border: "1px solid rgba(15,50,33,0.3)" }}>
          <Send size={12} className="mr-1" /> Send $1,500 Invoice
        </Button>
      )}
      {sub.concierge_final_paid && <span className="status-badge paid">Concierge Complete</span>}
      {sub.status === "approved" && <span className="status-badge approved">Approved</span>}
      {sub.status === "rejected" && <span className="status-badge rejected">Rejected</span>}
      {sub.payment_status === "paid" && !sub.followup_sent && sub.status !== "rejected" && sub.status !== "new" && sub.status !== "pending_approval" && (
        <Button size="sm" variant="ghost" onClick={() => onFollowUp(sub.id)} data-testid={`followup-${sub.id}`} className="h-6 px-2 text-xs" style={{ color: "#6366F1", border: "1px solid rgba(99,102,241,0.3)" }}>
          <Mail size={12} className="mr-1" /> Send Follow-Up
        </Button>
      )}
      {sub.followup_sent && <span className="status-badge" style={{ background: "rgba(99,102,241,0.08)", color: "#6366F1", border: "1px solid rgba(99,102,241,0.2)" }}>Follow-Up Sent</span>}
      {sub.reminder_count > 0 && !sub.followup_sent && (
        <span className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>
          {sub.reminder_count} reminder{sub.reminder_count > 1 ? "s" : ""} sent
        </span>
      )}
    </div>
  );
}

// ===== Expanded Detail Row =====
export function DetailRow({ sub, noteText, onNoteChange, onNoteSubmit, noteSubmitting }) {
  return (
    <tr>
      <td colSpan={8} style={{ background: "var(--bg-tertiary)", padding: "20px" }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-xs uppercase tracking-wider mb-3" style={{ color: "var(--accent)" }}>Intake Details</p>
            <div className="space-y-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              <p><strong style={{ color: "var(--text-primary)" }}>Phone:</strong> {sub.phone}</p>
              <p><strong style={{ color: "var(--text-primary)" }}>Intended Use:</strong> {sub.intended_use?.replace(/_/g, " ")}</p>
              <p><strong style={{ color: "var(--text-primary)" }}>Quantity:</strong> {sub.quantity?.replace(/_/g, " ")}</p>
              <p><strong style={{ color: "var(--text-primary)" }}>Budget:</strong> {sub.budget?.replace(/_/g, " ")}</p>
              <p><strong style={{ color: "var(--text-primary)" }}>Compliance:</strong> {sub.compliance?.replace(/_/g, " ")}</p>
              {sub.referral_source && <p><strong style={{ color: "var(--text-primary)" }}>Referral:</strong> {sub.referral_source}</p>}
              <p><strong style={{ color: "var(--text-primary)" }}>Payment:</strong> {sub.payment_status || "None"} {sub.paid_amount ? `($${sub.paid_amount})` : ""}</p>
              <p className="text-xs" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>ID: {sub.id}</p>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider mb-3" style={{ color: "var(--accent)" }}>
              <MessageSquare size={12} className="inline mr-1" /> Notes
            </p>
            <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
              {(sub.notes || []).length === 0 && <p className="text-xs" style={{ color: "var(--text-tertiary)" }}>No notes yet</p>}
              {(sub.notes || []).map((note) => (
                <div key={`${note.created_at}-${note.admin_email}`} className="p-2 text-xs" style={{ background: "rgba(0,0,0,0.02)", border: "1px solid var(--border)" }}>
                  <p style={{ color: "var(--text-secondary)" }}>{note.text}</p>
                  <p className="mt-1" style={{ color: "var(--text-tertiary)", fontSize: "10px" }}>{note.admin_email} · {formatDate(note.created_at)}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={noteText}
                onChange={(e) => onNoteChange(e.target.value)}
                placeholder="Add a note..."
                data-testid={`note-input-${sub.id}`}
                className="flex-1 bg-transparent border-b py-1 px-0 text-xs outline-none"
                style={{ borderColor: "var(--border)", color: "var(--text-primary)" }}
                onKeyDown={(e) => e.key === "Enter" && onNoteSubmit(sub.id)}
              />
              <Button size="sm" onClick={() => onNoteSubmit(sub.id)} disabled={noteSubmitting} data-testid={`note-submit-${sub.id}`} className="h-6 px-3 text-xs" style={{ background: "var(--accent)", color: "#FFFFFF" }}>
                Add
              </Button>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ===== Submissions Table =====
export function SubmissionsTable({
  submissions, loading, expandedRow, onToggleRow,
  onStatusChange, onApprove, onReject, onUpgrade, onConciergeFinal, onFollowUp,
  noteText, onNoteChange, onNoteSubmit, noteSubmitting,
}) {
  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4" style={{ borderColor: "var(--accent)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (submissions.length === 0) {
    return <div className="p-12 text-center"><p style={{ color: "var(--text-tertiary)" }}>No submissions found</p></div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="admin-table" data-testid="submissions-table">
        <thead>
          <tr>
            <th>Client</th><th>Lane</th><th>Budget</th><th>Status</th><th>Tags</th><th>Actions</th><th>Date</th><th></th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((sub) => (
            <SubmissionRow
              key={sub.id}
              sub={sub}
              isExpanded={expandedRow === sub.id}
              onToggle={() => onToggleRow(sub.id)}
              onStatusChange={onStatusChange}
              onApprove={onApprove}
              onReject={onReject}
              onUpgrade={onUpgrade}
              onConciergeFinal={onConciergeFinal}
              onFollowUp={onFollowUp}
              noteText={noteText}
              onNoteChange={onNoteChange}
              onNoteSubmit={onNoteSubmit}
              noteSubmitting={noteSubmitting}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===== Single Submission Row =====
function SubmissionRow({
  sub, isExpanded, onToggle, onStatusChange,
  onApprove, onReject, onUpgrade, onConciergeFinal, onFollowUp,
  noteText, onNoteChange, onNoteSubmit, noteSubmitting,
}) {
  return (
    <>
      <tr data-testid={`row-${sub.id}`}>
        <td>
          <p className="font-medium" style={{ color: "var(--text-primary)", fontSize: "13px" }}>{sub.name}</p>
          <p className="text-xs mt-0.5" style={{ color: "var(--text-tertiary)" }}>{sub.email}</p>
        </td>
        <td><span className={`lane-badge ${sub.lane}`}>{LANE_LABELS[sub.lane] || sub.lane}</span></td>
        <td className="text-sm" style={{ color: "var(--text-secondary)" }}>{sub.budget?.replace(/_/g, " ")}</td>
        <td>
          <Select value={sub.status} onValueChange={(val) => onStatusChange(sub.id, val)}>
            <SelectTrigger className="w-32 h-7 text-xs bg-transparent border-[#E5E2DC]" data-testid={`status-${sub.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </td>
        <td>
          <div className="flex flex-wrap gap-1">
            {(sub.tags || []).length > 0
              ? sub.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2 py-0.5" style={{ background: "rgba(15,50,33,0.06)", color: "var(--primary)", border: "1px solid rgba(15,50,33,0.15)" }}>{tag}</span>
                ))
              : <span className="text-xs" style={{ color: "var(--text-tertiary)" }}>—</span>
            }
          </div>
        </td>
        <td>
          <RowActions sub={sub} onApprove={onApprove} onReject={onReject} onUpgrade={onUpgrade} onConciergeFinal={onConciergeFinal} onFollowUp={onFollowUp} />
        </td>
        <td className="text-xs" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)", whiteSpace: "nowrap" }}>
          {formatDate(sub.created_at)}
        </td>
        <td>
          <button onClick={onToggle} data-testid={`expand-${sub.id}`} className="p-1" style={{ color: "var(--text-tertiary)" }}>
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </td>
      </tr>
      {isExpanded && (
        <DetailRow sub={sub} noteText={noteText} onNoteChange={onNoteChange} onNoteSubmit={onNoteSubmit} noteSubmitting={noteSubmitting} />
      )}
    </>
  );
}

// ===== Stats Grid =====
export function StatsGrid({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
      <div className="stat-card"><div className="stat-value" data-testid="stat-total">{stats.total}</div><div className="stat-label">Total</div></div>
      <div className="stat-card"><div className="stat-value" style={{ color: "var(--success)" }} data-testid="stat-paid">{stats.paid}</div><div className="stat-label">Paid</div></div>
      {Object.entries(stats.by_lane || {}).map(([lane, count]) => (
        <div key={lane} className="stat-card">
          <div className="stat-value" data-testid={`stat-${lane}`}>{count}</div>
          <div className="stat-label">{LANE_LABELS[lane] || lane}</div>
        </div>
      ))}
    </div>
  );
}

// ===== Filters Bar =====
export function FiltersBar({ laneFilter, statusFilter, onLaneChange, onStatusChange }) {
  return (
    <div className="flex items-center gap-4 mb-6 p-4" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>Lane:</span>
        <Select value={laneFilter} onValueChange={onLaneChange}>
          <SelectTrigger className="w-32 h-8 text-xs bg-transparent border-[#E5E2DC]" data-testid="filter-lane"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {Object.keys(LANE_LABELS).map((l) => <SelectItem key={l} value={l}>{LANE_LABELS[l]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>Status:</span>
        <Select value={statusFilter} onValueChange={onStatusChange}>
          <SelectTrigger className="w-36 h-8 text-xs bg-transparent border-[#E5E2DC]" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
